import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Boxes, Loader2, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BG = "https://images.unsplash.com/photo-1688494930045-328d0f95efe9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBnZW9tZXRyaWMlMjBtaW5pbWFsaXN0JTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODI0ODY1NjZ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [hasShaken, setHasShaken] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resent, setResent] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNeedsVerify(false);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      if (err.response?.status === 403) setNeedsVerify(true);
      setShaking(true);
      setHasShaken(true);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email });
      setResent(true);
    } catch {}
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-[#0A0A0B] text-white p-6">
      <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/70 via-[#0A0A0B]/80 to-[#0A0A0B]" />
      <div
        data-testid="login-card"
        onAnimationEnd={() => setShaking(false)}
        className={`relative w-full max-w-sm bg-[#121214]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-[0_30px_80px_rgba(0,0,0,0.6)] ${shaking ? "animate-shake" : hasShaken ? "" : "animate-fade-up"}`}
      >
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
            <Boxes className="h-5 w-5" />
          </div>
          <span className="font-display font-extrabold text-xl">Portal GA</span>
        </div>
        <h1 className="font-display text-3xl font-bold mb-1 text-center">Bienvenido de nuevo</h1>
        <p className="text-zinc-500 mb-8 text-sm text-center">Inicia sesión en tu cuenta</p>
        <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Correo</label>
              <input
                data-testid="login-email"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@galletasaromaticas.com"
                className="mt-1.5 w-full bg-[#121214] border border-white/10 rounded-lg px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Contraseña</label>
              <div className="relative mt-1.5">
                <input
                  data-testid="login-password"
                  type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#121214] border border-white/10 rounded-lg px-3 py-2.5 pr-10 text-sm placeholder:text-zinc-600 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                />
                <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="login-toggle-password" tabIndex={-1}
                  aria-label={showPw ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}
            {needsVerify && (
              <button type="button" onClick={resend} disabled={resent} data-testid="login-resend-verification"
                className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 font-semibold py-2 rounded-lg transition-colors text-sm disabled:opacity-60">
                {resent ? "Correo reenviado ✓" : "Reenviar correo de verificación"}
              </button>
            )}
            <button
              data-testid="login-submit"
              type="submit" disabled={loading}
              className="w-full bg-primary hover:bg-[#FF5047] text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Iniciar sesión
            </button>
          </form>
          <p className="text-sm text-zinc-500 mt-6 text-center">
            ¿No tienes cuenta?{" "}
            <Link to="/register" data-testid="goto-register" className="text-primary font-semibold hover:underline">Regístrate</Link>
          </p>
      </div>
    </div>
  );
}
